<template>
  <div class="w-11/12 flex basis-1/5 justify-evenly items-center mx-auto pt-1 gap-2">
    <div v-if="props.includeCurrentPassword" class="w-full max-w-32 h-full flex flex-col items-center justify-center">
      <div class="w-full flex justify-center items-center flex-col relative">
        <div
          class="h-8 w-8 rounded-2xl border-[2px] border-solid border-white z-10 status-circle"
          :class="currentStatus"
        ></div>
        <div class="h-3 border-[2px] w-full rounded-md absolute status-bar-outline" :class="currentStatus">
          <div class="h-full rounded-[2px] status-bar-fill" :class="currentStatus"></div>
        </div>
        <i
          class="z-20 text-white absolute"
          :class="{ 'icon-x': currentStatus === 'invalid', 'icon-check': currentStatus === 'valid' }"
        ></i>
      </div>
      <div text-center class="mt-2 status-bar-label" :class="currentStatus">Current</div>
    </div>

    <div class="w-full max-w-32 h-full flex flex-col items-center justify-center">
      <div class="w-full flex justify-center items-center flex-col relative">
        <div
          class="h-8 w-8 rounded-2xl border-[2px] border-solid border-white z-10 status-circle"
          :class="newStatus"
        ></div>
        <div class="h-3 border-[2px] w-full rounded-md absolute status-bar-outline" :class="newStatus">
          <div class="h-full rounded-[2px] status-bar-fill" :class="newStatus"></div>
        </div>
        <i
          class="z-20 text-white absolute"
          :class="{ 'icon-x': newStatus === 'invalid', 'icon-check': newStatus === 'valid' }"
        ></i>
      </div>
      <div text-center class="mt-2 status-bar-label" :class="newStatus">New</div>
    </div>

    <div class="w-full max-w-32 h-full flex flex-col items-center justify-center">
      <div class="w-full flex justify-center items-center flex-col relative">
        <div
          class="h-8 w-8 rounded-2xl border-[2px] border-solid border-white z-10 status-circle"
          :class="confirmStatus"
        ></div>
        <div class="h-3 border-[2px] w-full rounded-md absolute status-bar-outline" :class="confirmStatus">
          <div class="h-full rounded-[2px] status-bar-fill" :class="confirmStatus"></div>
        </div>
        <i
          class="z-20 text-white absolute"
          :class="{ 'icon-x': confirmStatus === 'invalid', 'icon-check': confirmStatus === 'valid' }"
        ></i>
      </div>
      <div text-center class="mt-2 status-bar-label" :class="confirmStatus">Confirm</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Ref, ref } from 'vue';
import { FormControl } from '@/composables/use-form-control';
import { watchDebounced } from '@vueuse/core';

export type Status = 'valid' | 'invalid' | 'unset';

const currentStatus: Ref<Status> = ref('unset');
const newStatus: Ref<Status> = ref('unset');
const confirmStatus: Ref<Status> = ref('unset');

const props = withDefaults(
  defineProps<{
    currentPassword?: FormControl;
    newPassword: FormControl;
    confirmPassword: FormControl;
    includeCurrentPassword?: boolean;
  }>(),
  {
    includeCurrentPassword: true,
  }
);

watchDebounced(
  [() => props.newPassword.value, () => props.newPassword.valid],
  () => {
    newStatus.value = !props.newPassword.value ? 'unset' : props.newPassword.valid ? 'valid' : 'invalid';
  },
  { debounce: 400 }
);

watchDebounced(
  [() => props.currentPassword?.value, () => props.currentPassword?.valid],
  () => {
    currentStatus.value = !props.currentPassword?.value ? 'unset' : props.currentPassword.valid ? 'valid' : 'invalid';
  },
  { debounce: 400 }
);

watchDebounced(
  [() => props.confirmPassword.value, () => props.confirmPassword.valid],
  () => {
    confirmStatus.value = !props.confirmPassword.value ? 'unset' : props.confirmPassword.valid ? 'valid' : 'invalid';
  },
  { debounce: 400 }
);
</script>

<style scoped>
.status-circle {
  transition: background-color 1s;
  background-color: #d6d6d6;

  &.valid {
    background-color: #4aa346;
  }
  &.invalid {
    background-color: #e3515f;
  }
}

.status-bar-outline {
  border-color: #d6d6d6;
  transition: border-color 1s;

  &.valid {
    border-color: #4aa346;
  }
  &.invalid {
    border-color: #e3515f;
  }
}

.status-bar-fill {
  transition: width 1s, color 1s;
  background-color: #98d196;
  width: 0%;
  border-radius: 16px;

  &.valid {
    width: 100%;
    background-color: #98d196;
  }
  &.invalid {
    width: 0%;
    background-color: #ee939c;
  }
}

.status-bar-label {
  color: #d6d6d6;
  transition: color 1s;
  font-weight: 500;

  &.valid {
    color: #347231;
  }
  &.invalid {
    color: #9f3943;
  }
}
</style>
